import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

const BASE_URL = (import.meta.env.VITE_API_URL || "") + "/api";

// ─── Token storage ────────────────────────────────────────────────────────────
// Two layers, in priority order:
//   1. sessionStorage — short-lived impersonation tokens. Per-tab scope means
//      opening "View as user" in a new tab does NOT clobber the admin's own
//      login in their original tab.
//   2. localStorage — the user's real long-lived session. Survives reloads.
// When BOTH exist (admin tab opens impersonation as a child window that
// somehow shares state — shouldn't happen, but defence in depth), sessionStorage
// wins so the impersonation always behaves as the impersonated user.
const ACCESS_KEY = "cybro_access";
const REFRESH_KEY = "cybro_refresh";
const IMP_ACCESS_KEY = "cybro_imp_access";
const IMP_REFRESH_KEY = "cybro_imp_refresh";
const IMP_META_KEY = "cybro_imp_meta"; // {email, impersonator_email, expires_at}

export interface ImpersonationMeta {
  email: string;
  impersonator_email: string;
  expires_at: string;
}

export const tokenStore = {
  getAccess: (): string | null =>
    sessionStorage.getItem(IMP_ACCESS_KEY) || localStorage.getItem(ACCESS_KEY),
  getRefresh: (): string | null =>
    sessionStorage.getItem(IMP_REFRESH_KEY) || localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh?: string) => {
    // Real login writes to localStorage; impersonation goes through
    // setImpersonation() below.
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    // If we're impersonating, only kill the impersonation layer — leave the
    // admin's real session in localStorage alone (different tab).
    if (sessionStorage.getItem(IMP_ACCESS_KEY)) {
      sessionStorage.removeItem(IMP_ACCESS_KEY);
      sessionStorage.removeItem(IMP_REFRESH_KEY);
      sessionStorage.removeItem(IMP_META_KEY);
      return;
    }
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  setImpersonation: (access: string, refresh: string, meta: ImpersonationMeta) => {
    sessionStorage.setItem(IMP_ACCESS_KEY, access);
    sessionStorage.setItem(IMP_REFRESH_KEY, refresh);
    sessionStorage.setItem(IMP_META_KEY, JSON.stringify(meta));
  },
  getImpersonation: (): ImpersonationMeta | null => {
    const raw = sessionStorage.getItem(IMP_META_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as ImpersonationMeta; } catch { return null; }
  },
  isImpersonating: (): boolean => !!sessionStorage.getItem(IMP_ACCESS_KEY),
};

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

// Attach Authorization header
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single in-flight refresh promise to avoid stampedes when multiple
// requests fail with 401 at the same time.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refresh = tokenStore.getRefresh();
      if (!refresh) throw new Error("No refresh token");
      const { data } = await axios.post(`${BASE_URL}/auth/token/refresh/`, {
        refresh,
      });
      tokenStore.set(data.access, data.refresh);
      return data.access as string;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Auto-refresh on 401 once per request
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _skipRefresh?: boolean;
    };

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original._skipRefresh &&
      !original.url?.includes("/auth/token/")
    ) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(original);
      } catch (refreshError) {
        tokenStore.clear();
        // Bubble up so router/store can redirect to login
        window.dispatchEvent(new CustomEvent("auth:expired"));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Convenience for non-API requests (e.g. raw axios on auth endpoints)
export { axios };
